import { Button, Drawer, Form, Input, Row, Select } from "antd";
import React, { useEffect } from "react";
import { CURRENCIES } from "@lib/constants/currencies";
import { TelegramBotConfig } from "@lib/types/TelegramChatBot";
import { FormattedMessage, useIntl } from "react-intl";
import { FaPlus } from "react-icons/fa";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const { Option } = Select;

interface IQuickDrawerProps {
  onCloseFunc: () => void;
  visibleState: boolean;
  submitLoading: boolean;
  onNewSubmit: (formValues: TelegramBotConfig) => void;
  selectedTelegramBotConfig?: TelegramBotConfig;
  handleEditTelegramBotConfig: (config: TelegramBotConfig) => void;
}

const QuickDrawer: React.FC<IQuickDrawerProps> = ({
  onCloseFunc,
  visibleState,
  submitLoading,
  onNewSubmit,
  selectedTelegramBotConfig,
  handleEditTelegramBotConfig,
}) => {
  const [form] = Form.useForm();
  const { messages } = useIntl();
  const resetForm = (): void => {
    form.resetFields();
  };

  const onFinish = (values: TelegramBotConfig): void => {
    if (selectedTelegramBotConfig) {
      delete selectedTelegramBotConfig.enabled;

      const payload: TelegramBotConfig = {
        ...selectedTelegramBotConfig,
        ...values,
      };
      handleEditTelegramBotConfig(payload);
    } else {
      onNewSubmit(values);
    }
  };

  const initialValues = selectedTelegramBotConfig;

  useEffect(() => {
    resetForm();
  }, [visibleState]);

  return (
    <Drawer
      title={
        <FormattedMessage
          id={
            selectedTelegramBotConfig
              ? "common.editTitle"
              : "common.createTitle"
          }
          values={{ title: messages["common.telegramBotConfig"] as string }}
        />
      }
      onClose={onCloseFunc}
      open={visibleState}
      getContainer={"body"}
      width={640}
      footer={
        <Row justify={"end"}>
          <Button type="text" onClick={resetForm}>
            {messages["common.reset"] as string}
          </Button>
          <Button
            color="blue"
            variant="solid"
            type="primary"
            loading={submitLoading}
            onClick={form.submit}
          >
            {messages["common.submit"] as string}
          </Button>
        </Row>
      }
    >
      <Form
        initialValues={initialValues}
        layout="vertical"
        form={form}
        onFinish={onFinish}
        size="middle"
      >
        <Form.Item
          label={messages["TELEGRAM.TELEGRAM_BOT_CONFIG_NAME"] as string}
          name="name"
          // rules={[RULES.required]}
          className="my-0"
          tooltip={
            messages["TELEGRAM.TELEGRAM_BOT_CONFIG_NAME_TOOLTIP"] as string
          }
        >
          <Input
            placeholder={
              messages["TELEGRAM.TELEGRAM_BOT_CONFIG_NAME"] as string
            }
          />
        </Form.Item>

        <Form.Item
          label={messages["TELEGRAM.TELEGRAM_BOT_APP_URL"] as string}
          name="appUrl"
          // rules={[RULES.required]}
          className="my-0"
          tooltip={messages["TELEGRAM.TELEGRAM_BOT_APP_URL_TOOLTIP"] as string}
        >
          <Input
            placeholder={messages["TELEGRAM.TELEGRAM_BOT_APP_URL"] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages["TELEGRAM.TELEGRAM_BOT_CONFIG_TOKEN"] as string}
          name="token"
          // rules={[RULES.required]}
          className="my-0"
          tooltip={
            messages["TELEGRAM.TELEGRAM_BOT_CONFIG_TOKEN_TOOLTIP"] as string
          }
        >
          <Input
            placeholder={
              messages["TELEGRAM.TELEGRAM_BOT_CONFIG_TOKEN"] as string
            }
          />
        </Form.Item>

        <Form.Item
          label={messages["TELEGRAM.TELEGRAM_BOT_URL"] as string}
          name="botUrl"
          // rules={[RULES.required]}
          className="my-0"
          tooltip={messages["TELEGRAM.TELEGRAM_BOT_URL_TOOLTIP"] as string}
        >
          <Input
            placeholder={messages["TELEGRAM.TELEGRAM_BOT_URL"] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages["common.paymentProvider"] as string}
          tooltip={messages["TELEGRAM.PAYMENT_PROVIDER_TOOLTIP"] as string}
        >
          <Form.List name="paymentProviders">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div
                    key={`payment${field.key}`}
                    style={{
                      display: "flex",
                      flex: 1,
                      gap: 8,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Form.Item
                      {...field}
                      noStyle
                      name={[field.name, "name"]}
                      // rules={[RULES.required]}
                    >
                      <Input
                        placeholder={
                          messages["TELEGRAM.PAYMENT_NAME"] as string
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      noStyle
                      name={[field.name, "token"]}
                      // rules={[RULES.required]}
                    >
                      <Input
                        placeholder={
                          messages["TELEGRAM.PAYMENT_TOKEN"] as string
                        }
                      />
                    </Form.Item>

                    <ActionButton
                      type="close"
                      noLabel
                      noBg
                      size="small"
                      onClick={() => remove(field.name)}
                    />
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<FaPlus />}
                  >
                    {messages["TELEGRAM.ADD_NEW_PAYMENT"] as string}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={messages["common.supportedCurrencies"] as string}
          tooltip={messages["TELEGRAM.SUPPORTED_CURRENCIES_TOOLTIP"] as string}
        >
          <Form.List name={["extraData", "supportedCurrencies"]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div
                    key={`currency${field.key}`}
                    style={{
                      display: "flex",
                      flex: 1,
                      gap: 8,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Form.Item
                      noStyle
                      name={[field.name]}
                      // rules={[RULES.required]}
                    >
                      <Select
                        showSearch
                        placeholder={messages["common.selectOne"] as string}
                        optionLabelProp="label"
                        filterOption={true}
                        optionFilterProp="label"
                        aria-autocomplete="none"
                      >
                        {Object.keys(CURRENCIES).map((key) => (
                          <Option key={key} label={CURRENCIES[key]} value={key}>
                            {CURRENCIES[key]}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <ActionButton
                      type="close"
                      noLabel
                      noBg
                      size="small"
                      onClick={() => remove(field.name)}
                    />
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<FaPlus />}
                  >
                    {messages["TELEGRAM.ADD_NEW_CURRENCY"] as string}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default QuickDrawer;
